import { useCallback, useRef, useEffect, useReducer, useState } from 'react';
import { createPortal } from 'react-dom';
import { resolveCameraAvailableForConfig, resolveRuntimeDisplay } from './runtime-display-mode';
import {
  cleanupStream,
  getVideoUrl,
  isMotionCaptureMode,
  persistExerciseType,
  persistSetCeilingSeconds,
  readStoredExerciseType,
  readStoredSetCeilingSeconds,
  resolveExecutionMode,
  resolveExerciseReadoutFlags,
  resolveExerciseType,
  resolveRemainingSetCeilingTime,
  resolveScoreStyles,
  resolveSessionSuspended,
  resolveSetCeilingSeconds,
  resolveTimerFreezePolicy,
  SESSION_WALL_TIMER_KEY,
  TIMER_FREEZE_POLICY,
} from './helpers';
import {
  addTimer,
  deleteTimer,
  getTimer,
  pauseTimer,
  isGreenRectangleColor,
  removeFromLocalStorageInPerformance,
  resolveRectangleColor,
  resumeTimer,
  timers,
} from '../../utilis/helpers';
import { usePostSound } from '../../zustand/post-sound';
import { exerciseReducer, exercisesStats } from '../../reducers/exercises-reducer';
import { initialStates } from '../../modules/exercsies';
import {
  screenSizes,
  SettingMode,
  rectangleFlag,
  DEFAULT_INTRO_INTERVAL_SECONDS,
  STAGE_STATUS,
  exerciseType as exerciseTypes,
} from '../../constants/constants';
import { useWindowDimensions } from '../../hooks';
import { useDevice } from '../../zustand/devices-store';
import { usePoseWorkerStore } from '../../zustand/pose-worker-store';
import { useDebugOverlays } from '../../zustand/debug-overlays-store';
import { useSessionMetadataStore } from '../../zustand/session-metadata-store';
import {
  AudioSection,
  AvocatoLoading,
  ExerciseSettingModal,
  RestAndNextConfiguration,
  VideoAndCommentSide,
  WebcamAndSkelatonSide,
  InPerformanceBorderContainer,
  usePostPerformance,
  usePredict,
  useRedLandMarks,
  useRecordingVideo,
  useSetPostData,
  useWebSocketConnection,
} from '../../pathes/exercises/index';
import { useTimer } from 'react-use-precision-timer';
import { useNavigate, useSearchParams } from 'react-router';
import { clearLatestRecordingPayload } from './hooks/use-recording-video';
import { stopCamera } from '../../services/camera-manager';
import { useIntroCountdown } from './hooks/use-intro-countdown';
import ExerciseIntro from '../../components/home/exercise-intro';
import ScoreAndFpsOverlay from './components/score-and-fps-overlay';
import ExerciseTopBar from './components/exercise-top-bar';
import PreStartCountdown from './components/pre-start-countdown';
import HoldTelemetryOverlay from './components/hold-telemetry-overlay';
import SetCeilingCountdown from './components/set-ceiling-countdown';
import { ENV } from '@config/env';
import { FEATURES } from '@config/features';
import AspectRatioOverlay from './components/aspect-ratio-overlay';
import useMediaAspectRatio from './hooks/use-media-aspect-ratio';
import MobileExerciseStatBar from '../../components/mobile-exercise-stat-bar';
import InPerformanceText from '../../components/home/in-performance-text';
import Counter from '../../components/counter/inedx';
import CutDownTimer from '../../components/cutdown-timer';
import QualityChip from '../../components/quality-chip';
import LoadingElement from '../../components/loading-element';
import useRealtimeVoice from '../../hooks/use-realtime-voice';
import useConnectionStatus from './hooks/use-connection-status';
import useNetworkQuality from './hooks/use-network-quality';
import ConnectionStatusStack from './components/connection-status-stack';
import {
  ConnectionDetailsButton,
  ConnectionDetailsPanel,
} from './components/connection-details-panel';
import { VOICE_COMPANION_MODE, isVoiceCompanionMode } from '@/constants/voiceCompanion';
import { markRealtimeLatencySignalReceived } from '../../services/realtime-voice';
import {
  pauseRealtimePlayback,
  resumeRealtimePlayback,
} from '../../services/realtime-connection-store';
import { resolveRemainingExerciseTime } from '../../utilis/helpers';
import tw from 'twin.macro';
import { css } from 'styled-components';
import { useTranslation } from 'react-i18next';

// Portrait mobile only: the runtime is a fixed, non-scrolling surface there.
// h-screen (100vh) is the iOS *static* viewport — taller than what's visible —
// so the stacked layout ran under Safari's toolbar and the page scrolled,
// exposing blank space below the reference video. dvh measures the visible
// viewport; the vh line is the fallback for engines without dvh and has to stay
// a separate declaration, since a `tw` class pair collapses into one height key.
const mobileViewportHeight = css`
  height: 100vh;
  height: 100dvh;
`;

// Upper bound on how long the socket waits for getUserMedia to actually
// resolve or fail (see cameraCheckSettled below) before opening anyway — a
// stuck permission prompt or a browser that never calls either callback must
// not deadlock the session forever.
const CAMERA_CHECK_TIMEOUT_MS = 6000;

// Where a backgrounded session lands, and how long it may stay hidden first.
// Change the destination here — it is read in exactly one place, the effect
// below. /camera-permission rather than a deeper screen because the camera is
// the thing the return has to re-establish.
const HIDDEN_EXIT_ROUTE = '/camera-permission';
// The grace period is the point: visibilitychange cannot tell a minimise from
// a glance at another tab or a phone notification, so an immediate exit would
// end a patient's exercise over a two-second look away.
const HIDDEN_EXIT_DELAY_MS = 30000;

// Keeps the camera element mounted and decoding while nothing of it is visible:
// used by the INTRO screen (the backend needs evaluation data to leave that
// stage) and by every offline layout, where hiding the cell must not tear the
// MediaStream down — an action-7 flip back to MOTION_CAPTURE would then need a
// fresh getUserMedia, and on some browsers a fresh permission prompt, mid-set.
const offscreenMountStyle = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  opacity: 0,
  pointerEvents: 'none',
};

// Desktop keeps the original plain h-screen page — none of the mobile viewport
// work above applies there, and it never had the problem.
const shellBaseStyle = tw`flex flex-col`;
const desktopShellStyle = tw`relative h-screen`;
const mobileShellStyle = tw`fixed inset-x-0 top-0 overflow-hidden`;

// Landscape phone (Figma 2058-14852): white surface, the two media cards side by
// side with a hairline gap, and the horizontal padding tied to the safe area —
// on a notched iPhone in landscape the notch eats the leading inset, which is
// exactly the gutter the design draws. Same dvh-after-vh rule as above.
const landscapeSurfaceStyle = css`
  position: relative;
  flex: 1 1 0%;
  min-height: 0;
  display: grid;
  gap: 6px;
  overflow: hidden;
  background: #ffffff;
  padding: 8px max(10px, env(safe-area-inset-right)) 8px max(10px, env(safe-area-inset-left));
`;

// Each media cell is a card: rounded, clipped, and the positioning context for
// VideoAndCommentSide's own absolute-positioned overlay (see fitsContainer).
const landscapeCardStyle = tw`relative min-h-0 w-full h-full overflow-hidden rounded-[12px] bg-black`;

// The coaching line sits at the bottom edge of the CAMERA pane, centred on the
// page — it deliberately spills over the reference pane in the two-column
// layouts rather than being centred inside the camera column. Each anchor is
// absolute inside the branch's own full-page container, so "page centre" is
// `left-1/2` in all three; only the vertical edge differs:
//   desktop    — the camera cell is full height, so the page bottom is that edge
//   landscape  — clears the card padding (8px) plus a little breathing room
//   portrait   — the camera is the top half of the content area, so the edge is
//                that container's own 50% mark, not the viewport's
const transcriptAnchorStyle = tw`absolute left-1/2 -translate-x-1/2 z-[300]`;
const desktopTranscriptStyle = [transcriptAnchorStyle, tw`bottom-6`];
const landscapeTranscriptStyle = [transcriptAnchorStyle, tw`bottom-4`];
const stackedTranscriptStyle = [transcriptAnchorStyle, tw`bottom-1/2 mb-3`];

const ExercisePage = () => {
  const { t } = useTranslation('exercise');
  const [searchParams] = useSearchParams();
  // Carried on the URL all the way from the prescription builder
  // (/camera-permission → /try-demo → /exercise); use-websocket-connection reads
  // it here to put on the socket's config message, with localStorage as the
  // fallback for a reload that dropped the query string.
  const voiceCompanionModeFromUrl = searchParams.get('voiceCompanionMode');
  const initialConfig = JSON.parse(localStorage.getItem('initialConfig'));
  const exerciseId = localStorage.getItem('exerciseId');
  // Where to send the patient when the session ends: `completeUrl` on a
  // normal finish, `exitUrl` on "Complete later". Fetched on /try-demo from
  // the session-media endpoint (getSessionMedia) and written into this same
  // initialConfig object — the backend no longer restates them on the socket.
  const completeUrl = initialConfig?.completeUrl;
  const exitUrl = initialConfig?.exitUrl;
  const activeSessionId = initialConfig?.sessionId || null;

  // Covers a reload/deep-link straight onto /exercise, bypassing
  // /camera-permission and /try-demo (which both fetch this too) — dedupes
  // against that fetch by session id.
  useEffect(() => {
    useSessionMetadataStore.getState().fetchMetadata(activeSessionId);
  }, []);
  // The last live cameraIsActive value (set on every change, including camera
  // errors / a track ending mid-exercise) beats cameraPermissionGranted, which
  // only ever records that permission was granted at some point — trusting it
  // alone would make a reload right after the camera closes mid-exercise
  // wrongly assume the camera is back, instead of resuming in the same
  // offline (no-camera) mode it was just in.
  const storedCameraIsActive = localStorage.getItem('cameraIsActive');
  const [state, dispatch] = useReducer(exerciseReducer, initialStates, init => ({
    ...init,
    cameraIsActive:
      storedCameraIsActive !== null
        ? storedCameraIsActive === 'true'
        : localStorage.getItem('cameraPermissionGranted') === 'true',
  }));
  // `execution_mode` is per exercise, not per session: the prescription writes it
  // into the runtime settings, the backend restates it on every intro payload and
  // on every frame, and action 7 can flip it for one exercise. There is no
  // session-level camera boolean any more.
  const prescribedMotionCapture = isMotionCaptureMode(initialConfig?.settings);
  const backendMotionCapture = isMotionCaptureMode(state.introResult);
  // All three must agree before this exercise counts as motion-captured: the
  // prescribed mode, the mode on its intro payload, and the mode the backend
  // reports as actually in effect on the live frames.
  const isMotionCaptureExercise =
    prescribedMotionCapture && backendMotionCapture && isMotionCaptureMode(state);
  // The single source for what the runtime shows — see runtime-display-mode.js
  // for the four execution_mode x camera combinations.
  const display = resolveRuntimeDisplay({
    isMotionCapture: isMotionCaptureExercise,
    hasCamera: state.cameraIsActive,
  });
  const showCameraSide = display.showsCameraCell;
  // A camera that can actually drive pose detection: online (motion capture +
  // a live media source) and MediaPipe not switched off from the pause modal.
  // Distinct from the `cameraAvailable` sent on the config message, which
  // reports the device state alone.
  const cameraAvailable = display.isOnline && state.mediaPipeEnabled;

  // Small helper so every piece of page state stays a reducer field, dispatched
  // through the same exercisesStats keys used everywhere else in this reducer.
  // Each setter's identity is cached (dispatch is stable across renders) so it
  // doesn't churn every render — a churning identity here breaks any effect
  // that lists one of these setters as a dependency (e.g. useRecordingVideo's
  // startRecording effect), causing an infinite render loop.
  const settersRef = useRef({});
  const setField = key => {
    if (!settersRef.current[key]) {
      settersRef.current[key] = value => dispatch({ type: exercisesStats[key], payload: value });
    }
    return settersRef.current[key];
  };
  const setIsCameraTranslated = setField('isCameraTranslated');
  const setIsCConfigure = setField('isCConfigure');
  const setTime = setField('time');
  const setToggleMirror = setField('toggleMirror');
  const setRecordingDate = setField('recordingDate');
  const setFrontendFps = setField('frontendFps');
  const setMediaSourceType = setField('mediaSourceType');
  const setUploadedVideoUrl = setField('uploadedVideoUrl');
  const setPreStartCountdown = setField('preStartCountdown');
  const setIsPreStartCountdownComplete = setField('isPreStartCountdownComplete');
  const setIsRecordingActive = setField('isRecordingActive');
  const setVideoReady = setField('videoReady');
  const setIntroResult = setField('introResult');
  const setDrawOnImage = setField('drawOnImage');

  const hideTimer = localStorage.getItem('hideTime');
  const parseTimer = JSON.parse(hideTimer);
  const videoRef = useRef();
  const canvasElement = useRef(null);
  const soundRef = useRef(null);
  const canvasForObjectDetectionRef = useRef(null);
  const SectorCanvasRef = useRef(null);
  const videoPlayerRef = useRef(null);
  const canvasForObjectRef = useRef(null);
  const canvasRef = useRef();
  const isSoundPlay = useRef(null);
  const audioSectionRef = useRef();
  const uploadVideoInputRef = useRef(null);
  // Overlay canvas the live pose skeleton is drawn onto (sits over the webcam).
  const skeletonCanvasRef = useRef(null);
  // Keep one stable React owner for the camera throughout the session. The
  // portal container can move between visual slots without unmounting the
  // camera, stopping its tracks, or requesting getUserMedia again.
  const [cameraPortalContainer] = useState(() => {
    if (typeof document === 'undefined') {
      return null;
    }

    const element = document.createElement('div');
    element.dataset.exerciseCameraPortal = 'true';
    element.style.cssText = 'position:relative;width:100%;height:100%;overflow:hidden;';
    return element;
  });
  const attachCameraPortal = useCallback(
    hostElement => {
      if (
        hostElement &&
        cameraPortalContainer &&
        cameraPortalContainer.parentNode !== hostElement
      ) {
        hostElement.appendChild(cameraPortalContainer);
      }
    },
    [cameraPortalContainer]
  );
  // Whether MediaPipe currently sees the person in frame (drives the red border).
  const [personInFrame, setPersonInFrame] = useState(true);

  const audioDevice = useDevice(state => state.audioDevice);
  const updatedWithExtensions = getVideoUrl();

  const { clientWindowWidth, clientWindowHeight } = useWindowDimensions();
  // Mobile in portrait only — landscape mobile has its own layout below.
  const isPortraitMobile =
    clientWindowWidth < screenSizes.lg && clientWindowHeight > clientWindowWidth;
  // Landscape phone/tablet: the redesign (Figma 2058-14852) makes this two rounded
  // cards on a white surface — camera left, reference right — with the readouts as
  // chips in each card's top corners instead of one stacked column, and the page
  // inset by the notch's safe area. Desktop keeps the full-bleed grid it had.
  const isLandscapeMobile =
    clientWindowWidth < screenSizes.lg && clientWindowWidth > clientWindowHeight;

  const closeCameraAfterNavigate = () => {
    cleanupStream({ videRef: videoRef, dispatch });
  };

  // Every deliberate way out of the session already calls
  // closeCameraAfterNavigate, but leaving this page is not always deliberate:
  // the browser back button, a route change from anywhere else, and an error
  // boundary tearing the tree down all unmount this component without going
  // through any of those handlers, and the camera light would stay on. Runs
  // last, with no dispatch — the reducer is gone by then, so only the hardware
  // release matters. Empty deps: this must fire on unmount, not on every
  // render, and stopCamera is idempotent so overlapping with an explicit exit
  // is harmless.
  useEffect(() => {
    return () => {
      videoRef.current = null;
      stopCamera('exercise-unmount');
    };
  }, []);

  const handleSwitchToCamera = () => {
    if (state.uploadedVideoUrl) {
      URL.revokeObjectURL(state.uploadedVideoUrl);
    }
    setUploadedVideoUrl('');
    setMediaSourceType('camera');
    dispatch({ type: exercisesStats.cameraIsActive, payload: false });
  };

  const handleUploadVideoClick = () => {
    uploadVideoInputRef.current?.click();
  };

  const handleUploadedVideoChange = event => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (state.uploadedVideoUrl) {
      URL.revokeObjectURL(state.uploadedVideoUrl);
    }

    closeCameraAfterNavigate();
    const objectUrl = URL.createObjectURL(file);
    setUploadedVideoUrl(objectUrl);
    setMediaSourceType('upload');
  };

  // Which diagnostic layers the user wants drawn over the video (pause modal →
  // "On-screen details"). Persisted in localStorage and defaulted to hidden on
  // small viewports, where there's no room to spare over the feed.
  const debugOverlays = useDebugOverlays(state => state.overlays);

  // Live geometry of whatever MediaPipe is reading — the webcam or the uploaded
  // clip, both of which hang off videoRef — for the aspect-ratio readout.
  const mediaGeometry = useMediaAspectRatio({
    videoRef,
    enabled: Boolean(debugOverlays.aspectRatio),
  });

  const isPoseModelReady = usePoseWorkerStore(state => state.isReady);
  const poseModelError = usePoseWorkerStore(state => state.error);
  const retryPoseModel = usePoseWorkerStore(state => state.retry);

  const {
    data,
    connectionStatus,
    inPerformance,
    responseLandMarks,
    sendJsonMessage,
    shapeColor,
    isRestStage,
    isFalseStage,
    voiceResult,
    isPostStage,
    handlelReConnect,
    setReconnect,
    counter,
    currentScore,
    currentFps,
    isUpComingStage,
    setResult,
    upComingStageResult,
    isPaseStage,
    pauseStageData,
    currentPauseStageOrder,
    socketExecutionMode,
    holdTelemetry,
    isHoldTimerRunning,
    isTimedRepsTimerRunning,
    reConnect,
    IntroResult,
    isIntroStage,
    exerciseSummary,
    stage,
    triggerDispatched,
    socketOpenCount,
    forceReconnect,
  } = useWebSocketConnection({
    initialConfig,
    time: state.time,
    voiceCompanionMode: voiceCompanionModeFromUrl,
    // Sent once on the config message: whether a live media source is really
    // behind the prescribed execution_mode (see runtime-display-mode.js).
    //
    // Gated on isMotionCaptureExercise too, not just the raw device flag: a
    // device camera can be active for a NO_MOTION_CAPTURE exercise (leftover
    // permission/stream from an earlier one, or the offscreen camera element
    // this page keeps mounted) while nothing about the exercise uses it. Told
    // `cameraAvailable: true` anyway, the backend keeps expecting real pose
    // landmarks that never arrive (this exercise only ever sends the empty-frame
    // heartbeat), eventually times out with close code 4002 — which now just
    // reopens the socket silently regardless of this flag (see onClose in
    // use-websocket-connection.jsx).
    cameraAvailable: resolveCameraAvailableForConfig(state) && isMotionCaptureExercise,
    // Don't open the session/start sending until the pose model has finished
    // loading — otherwise the backend starts the exercise before we can send
    // any real landmark data.
    //
    // For a motion-capture exercise, also wait on cameraCheckSettled: without
    // it, the config message read state.cameraIsActive before getUserMedia had
    // actually resolved — often still the stale value seeded from localStorage
    // on mount (see the reducer init below) — and sent a wrong cameraAvailable
    // that could never be corrected (the socket protocol has no re-config
    // message). Skipped for NO_MOTION_CAPTURE, which never waits on the camera
    // at all.
    enabled: isPoseModelReady && (!isMotionCaptureExercise || state.cameraCheckSettled),
    // Holds the socket closed while a lost stream is being reacquired, so a
    // reconnect cannot report `cameraAvailable: false` for a camera that is
    // seconds away from coming back (see the reducer field of the same name and
    // CAMERA_REACQUIRE_HOLD_MS in the hook). Backgrounding the tab loses the
    // capture session and the socket together, and the config message is the
    // only place the backend hears about the camera at all.
    cameraReacquiring: state.cameraReacquiring,
    // Camera geometry source for the config message (aspect-invariant matching).
    videoRef,
  });

  // A camera that comes back mid-socket cannot be reported on the socket that is
  // already open: the protocol has no re-config message, and the backend holds
  // the config message's `cameraAvailable` for the socket's whole lifetime, so a
  // socket that opened while the camera was down runs the WHOLE session as
  // NO_MOTION_CAPTURE. The `cameraReacquiring` hold above keeps that from
  // happening in the first place; this is the escape hatch for when it happened
  // anyway (the camera came back after the hold expired, or the socket stayed
  // open across the whole outage): reopen the socket so `onOpen` sends a config
  // that tells the truth.
  //
  // The cost is real and is accepted deliberately (see forceReconnect): the
  // in-flight set is replayed from its start and the backend raises a
  // session-exit webhook for the closed socket. A session stuck with no motion
  // capture until the patient reloads is worse.
  //
  // Only false -> true fires it. A camera that DIES needs no reopen — the
  // backend was told `true`, simply gets no landmarks, and this page drops to
  // the offline readouts locally.
  //
  // Deliberately the PRESCRIBED mode, not `isMotionCaptureExercise`: that one
  // folds in the mode the backend currently reports, so a demotion would feed
  // back into itself and the escape hatch could never fire. Both sides of the
  // comparison use this same expression, so it cannot oscillate.
  const reportableCameraAvailable = Boolean(state.cameraIsActive) && prescribedMotionCapture;
  // What the socket now open was opened with — captured on the first render it
  // is Open for, keyed by socketOpenCount so a reopen re-baselines instead of
  // comparing against the previous socket.
  const socketCameraBaselineRef = useRef({ socket: 0, value: null });
  useEffect(() => {
    if (connectionStatus !== 'Open') {
      return;
    }

    if (socketCameraBaselineRef.current.socket !== socketOpenCount) {
      socketCameraBaselineRef.current = {
        socket: socketOpenCount,
        value: reportableCameraAvailable,
      };
      return;
    }

    if (socketCameraBaselineRef.current.value !== false || !reportableCameraAvailable) {
      return;
    }

    // Baselined before the reopen, so a second render cannot fire it twice.
    socketCameraBaselineRef.current = { socket: socketOpenCount, value: true };
    forceReconnect('camera came back on a socket opened without one');
  }, [connectionStatus, forceReconnect, reportableCameraAvailable, socketOpenCount]);

  // The pause command is the ONLY thing that makes the backend send the exercise
  // list, and it was sent on whichever socket was open when the user paused. A
  // socket that dropped while the session sat paused — the idle timeout a paused
  // session invites, since it stops sending frames entirely — left the backend
  // running an exercise the user sees paused, and left the modal with nothing to
  // list. Re-send it once per new socket, while the modal is actually open.
  const lastPauseResyncRef = useRef(0);
  useEffect(() => {
    if (!state.openSettingModel) {
      // Nothing to resync — and this is also what keeps the modal's own opening
      // from double-sending: whichever handler opened it already sent the pause
      // on this socket.
      lastPauseResyncRef.current = socketOpenCount;
      return;
    }

    if (connectionStatus !== 'Open' || lastPauseResyncRef.current === socketOpenCount) {
      return;
    }

    lastPauseResyncRef.current = socketOpenCount;
    sendJsonMessage({ pause: { action: SettingMode.pause } });
  }, [connectionStatus, sendJsonMessage, socketOpenCount, state.openSettingModel]);

  // Bounded fallback for the enabled-gate above: if getUserMedia never calls
  // either callback (a permission prompt stuck open, an odd browser), the
  // session must still start rather than hang forever.
  useEffect(() => {
    if (state.cameraCheckSettled) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      dispatch({ type: exercisesStats.cameraCheckSettled, payload: true });
    }, CAMERA_CHECK_TIMEOUT_MS);

    return () => window.clearTimeout(timeoutId);
  }, [state.cameraCheckSettled]);

  const { postPerformance, postData } = useSetPostData(data, sendJsonMessage);

  // Voice agent (OpenAI Realtime over WebRTC). Always requested — the browser
  // never holds an OpenAI credential at all: it hands its SDP offer to our
  // backend, which opens the call, so there is nothing local to gate this on.
  //
  // The call itself is normally already open by the time this runs: it is
  // established on /camera-permission, and the store recognises the same
  // `sessionId::exerciseId` target and hands back the live call untouched. This
  // stays as the fallback for the one path that skips that page — a reload
  // straight onto /exercise — and to read the connection's state for the UI.
  const realtimeVoice = useRealtimeVoice(initialConfig?.sessionId ?? null, exerciseId ?? null);
  const isRealtimeActive = realtimeVoice.isOpen;

  // Patient-facing connection status — observation only (see
  // ./connection-status.js). Voice guidance counts as on exactly when the socket
  // config would say `live`: same URL param, same fallback as
  // use-websocket-connection, so the two can never disagree. The voice call
  // itself is requested either way; with guidance off its state is not the
  // patient's concern. `display.isOnline` here means what it always means on
  // this page — the exercise is tracked with the camera — not network state.
  const isVoiceGuidanceEnabled =
    (isVoiceCompanionMode(voiceCompanionModeFromUrl)
      ? voiceCompanionModeFromUrl
      : VOICE_COMPANION_MODE.live) === VOICE_COMPANION_MODE.live;
  // Development environment only for now (see config/features.js). Off, none
  // of it renders, no network probe runs, and the screen is exactly what it was.
  const showConnectionStatus = FEATURES.CONNECTION_STATUS;
  const networkState = useNetworkQuality({
    enabled: showConnectionStatus,
    isSocketOpen: connectionStatus === 'Open',
  });
  const connectionView = useConnectionStatus({
    socketStatus: connectionStatus,
    voiceEnabled: isVoiceGuidanceEnabled,
    voiceStatus: realtimeVoice.connectionStatus,
    hasVideoSource: Boolean(updatedWithExtensions),
    usesCamera: display.isOnline,
    isCameraReacquiring: Boolean(state.cameraReacquiring),
    isCameraMissing: display.isCameraMissingForMotionCapture,
    networkState,
  });
  const [isConnectionDetailsOpen, setIsConnectionDetailsOpen] = useState(false);
  const connectionDetailsButtonRef = useRef(null);
  const toggleConnectionDetails = useCallback(
    () => setIsConnectionDetailsOpen(isOpen => !isOpen),
    []
  );
  const openConnectionDetails = useCallback(() => setIsConnectionDetailsOpen(true), []);
  const closeConnectionDetails = useCallback(() => {
    setIsConnectionDetailsOpen(false);
    connectionDetailsButtonRef.current?.focus();
  }, []);

  // Every combination of stage + settings modal resolves to exactly one of
  // resume/pause here — there is no third "leave it as it was" outcome. The
  // previous version only ever paused on POST_PERFORMANCE and only ever
  // resumed on INTRO/IN_PERFORMANCE, so REST and EXERCISES_DONE (the
  // between-sets and between-exercises stages) fell through untouched: once
  // playback paused for any reason it stayed paused through those stages with
  // nothing left to resume it, since this effect only reruns when stage or
  // the modal flag actually changes.
  useEffect(() => {
    if (state.openSettingModel) {
      pauseRealtimePlayback('exercise:settings-modal');
      return;
    }

    if (stage === STAGE_STATUS.POST_PERFORMANCE) {
      pauseRealtimePlayback('exercise:post-performance');
      return;
    }

    resumeRealtimePlayback('exercise:stage-active');
  }, [stage, state.openSettingModel]);

  // The backend says "I just fired a voice event" → start the clock. The
  // monitor stops it when the first audible sample lands.
  useEffect(() => {
    if (triggerDispatched == null || triggerDispatched === false) {
      return;
    }

    realtimeVoice.clearLatestAssistantLatency();
    markRealtimeLatencySignalReceived(
      typeof triggerDispatched === 'number' ? triggerDispatched : undefined
    );
  }, [triggerDispatched]);

  const { setSoundPost } = usePostSound();

  const navigate = useNavigate();

  // Send a session that has been hidden too long to HIDDEN_EXIT_ROUTE.
  //
  // Leaving this page tears the session down — the socket closes, the camera
  // stops, and the backend runs its exit handling — so the delay is what keeps
  // a glance at another tab from ending an exercise.
  //
  // Two paths reach the exit, and the second is the one that actually fires in
  // practice: a hidden page has its timers throttled to about one tick a minute
  // and is frozen outright by some browsers, so the timer below cannot be
  // trusted to run while the page is away. Measuring the gap on the way back
  // catches exactly the case the timer sleeps through.
  const hiddenExitDoneRef = useRef(false);
  const hiddenAtRef = useRef(null);
  useEffect(() => {
    if (!FEATURES.HIDE_EXITS_SESSION) {
      return undefined;
    }

    let timeoutId = null;

    const clearPendingExit = () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const leaveSession = (via, hiddenForMs) => {
      // Both paths can reach here for the same absence — a timer that fired
      // late, then the return check — so the latch is what guarantees one
      // navigation. The unmount that follows removes the listener.
      if (hiddenExitDoneRef.current) {
        return;
      }

      hiddenExitDoneRef.current = true;

      // Carry this page's query string over, or the patient lands on a camera
      // screen that cannot re-enter the session: it authenticates every call
      // with `session_token` and relays `voiceCompanionMode`/`patientName`
      // onward to /try-demo. `sid` is the one param it needs that /exercise
      // never carries — try-demo drops it on the way in (see its
      // exerciseParams) — so it comes off the session config instead.
      const exitParams = new URLSearchParams(searchParams);
      if (!exitParams.get('sid') && activeSessionId) {
        exitParams.set('sid', activeSessionId);
      }
      const exitQuery = exitParams.toString();
      const exitTarget = exitQuery ? `${HIDDEN_EXIT_ROUTE}?${exitQuery}` : HIDDEN_EXIT_ROUTE;

      console.warn(`[hidden-exit] via ${via}, hidden ${hiddenForMs}ms — leaving for ${exitTarget}`);
      // replace, like every other navigation out of this page: Back must not
      // drop the patient into an exercise whose socket and camera are gone.
      navigate(exitTarget, { replace: true });
    };

    const handleVisibilityChange = () => {
      clearPendingExit();

      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        timeoutId = window.setTimeout(() => {
          timeoutId = null;
          leaveSession('timer', HIDDEN_EXIT_DELAY_MS);
        }, HIDDEN_EXIT_DELAY_MS);
        return;
      }

      const hiddenAt = hiddenAtRef.current;
      hiddenAtRef.current = null;

      if (hiddenAt === null) {
        return;
      }

      const hiddenForMs = Date.now() - hiddenAt;

      if (hiddenForMs >= HIDDEN_EXIT_DELAY_MS) {
        leaveSession('return', hiddenForMs);
        return;
      }

      console.warn(`[hidden-exit] back after ${hiddenForMs}ms — under the limit, staying`);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearPendingExit();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [navigate, searchParams]);

  // Real backend interval whenever it's there; the constant only covers a
  // payload that omits it, which otherwise left the intro countdown parked at
  // 0 (the hook treats a missing interval as "still loading").
  const backendIntroInterval = Number(state.introResult?.introInterval);
  const introInterval =
    Number.isFinite(backendIntroInterval) && backendIntroInterval > 0
      ? backendIntroInterval
      : DEFAULT_INTRO_INTERVAL_SECONDS;

  const { showIntro, countdown } = useIntroCountdown(
    state.videoReady,
    introInterval,
    stage,
    state.openSettingModel,
    state.time,
    // Identifies which exercise's intro this is, independent of the stage
    // enum/interval — see the reseed effect's comment in useIntroCountdown.
    // A primitive, not the introResult object itself: the backend re-sends
    // that object on every frame during INTRO, so the object reference
    // changes constantly and would reseed the timer every render, freezing
    // it at the full interval instead of counting down.
    state.introResult?.exerciseName
  );
  const exerciseName =
    localStorage.getItem('exerciseName') || state.introResult?.exerciseName || 'exercise';
  const scoreStyles = resolveScoreStyles(currentScore);
  const currentUserTimerMs = getTimer({ key: 'userTimer', timer: state.time }) ?? 0;
  const shouldRecordVideo =
    state.cameraIsActive && connectionStatus === 'Open' && state.isRecordingActive && !isPostStage;
  // Offline is now both axes, not just the camera: a NO_MOTION_CAPTURE exercise
  // with a live camera is offline too, because nothing is scoring it.
  const isOfflineMode = display.isOffline;
  // While the camera + backend are both actively tracking, show the live counter
  // (it starts from 0 for the new tracking session). Otherwise fall back to the
  // last known value instead of whatever `counter` currently holds, since it can
  // be stale/reset outside of an active tracking session.
  const isCameraTrackingSession = display.isOnline;
  const lastCounterRef = useRef(counter ?? 0);
  useEffect(() => {
    if (!isCameraTrackingSession && typeof counter === 'number') {
      lastCounterRef.current = counter;
    }
  }, [counter, isCameraTrackingSession]);
  const displayCounter = isCameraTrackingSession ? counter || 0 : lastCounterRef.current;
  // On mobile portrait there's only room to stack, not go side-by-side —
  // show the live webcam on top and the reference video below it (landscape
  // mobile keeps the side-by-side desktop layout, it already fits). Gated on
  // isPortraitMobile alone (not showCameraSide) because WebcamAndSkelatonSide
  // below is rendered unconditionally regardless of that flag.
  const isStackedMobileLayout = isPortraitMobile;
  const shouldShowPreStartCountdown = inPerformance && !state.openSettingModel;
  // Set synchronously by resetUserTimer (pause modal -> Restart) before the
  // dispatch that closes the modal, and consumed by the countdown effect below.
  // Declared up here rather than beside that effect because the render-time
  // fallback on the next line has to see it too — see the comment there.
  const skipNextPreStartCountdownRef = useRef(false);
  // Render the "3" immediately on the same render the stage flips to in-performance,
  // instead of waiting a render cycle for the effect below to dispatch it — otherwise
  // the countdown briefly flashes empty before showing up.
  //
  // The skip ref has to be part of this condition, not just the effect's. On the
  // render right after a restart closes the modal, `preStartCountdown` is null
  // (the modal's own close reset it) and `isPreStartCountdownComplete` is still
  // false, so `?? 3` would invent a countdown the restart just asked to skip.
  // Effects run child-first, so AudioSection would see null -> 3 and start the
  // get-ready clip a full render before the effect below could set the flags
  // that make it stop — the clip then plays on with no countdown on screen.
  const preStartCountdownValue =
    shouldShowPreStartCountdown &&
    !state.isPreStartCountdownComplete &&
    !skipNextPreStartCountdownRef.current
      ? (state.preStartCountdown ?? 3)
      : null;
  // Gate on the explicit "complete" flag rather than `!preStartCountdown` — the
  // countdown value is also `null` while idle/resting, so using it directly caused
  // userTimer to start the instant the stage flipped, before the countdown even ran.
  const canStartUserTimer = state.isPreStartCountdownComplete;
  // Every state where no frame may leave the client: the "3, 2, 1" get-ready
  // countdown (the user isn't performing yet) and the settings/pause modal.
  // Passed to usePredict, which tears its send intervals down instead of
  // letting frames pile up behind a pause.
  //
  // Deliberately NOT gated on isPaseStage: it is read off the last socket
  // message, and the backend only updates it in response to frames we send.
  // Blocking on it deadlocks — no frames, so no message, so the flag never
  // clears and sending never resumes. It is a backend-driven state, and the
  // backend already ignores/handles frames while it's paused.
  const isFrameSendingPaused = preStartCountdownValue !== null || state.openSettingModel;
  // Detection is gated separately from sending. The get-ready countdown must
  // keep detecting — that's what keeps the skeleton drawn over the 3, 2, 1, so
  // the user can see their framing while they get into position — it just must
  // not push those frames to the backend (finishTick drops them). The settings
  // /pause modal covers the camera entirely, so detection genuinely stops there.
  const isDetectionPaused = state.openSettingModel;
  // Border + skeleton coloring. Green is gone from both — there's no green
  // framing aid during the get-ready countdown any more, and a green
  // `rectangleColor` from the backend is dropped below like no color at all:
  //  - MediaPipe loses the person (empty landmarks) while performing → red;
  //  - the backend sends any other color → that color;
  //  - otherwise the border is hidden and the skeleton is drawn white.
  const isPersonMissing = inPerformance && canStartUserTimer && !personInFrame;
  // The border is a framing aid drawn around the camera feed, so it only makes
  // sense while there actually is one: the camera must be allowed (session
  // setting + backend per-movement flag) and running. With the camera off the
  // cell holds no live video at all, and a green/red frame around it reads as
  // a tracking state that isn't happening — never show it there.
  const isCameraFeedVisible = display.showsCameraCell;
  // Quality/score is pose-derived, so it only exists online. Under
  // NO_MOTION_CAPTURE the backend sends no score at all, and with no camera there
  // is nothing to score — either way the readout just sat at 0%, so it goes and
  // the totals stay (counter / timer during performance, completion rate in the
  // summary).
  //
  // The summary line uses the mode alone, deliberately without the camera: it is
  // shown after an uploaded clip has ended, which flips cameraIsActive back to
  // false, and the score it reports was earned while the clip was still playing.
  const isCameraScoringAllowed = prescribedMotionCapture && backendMotionCapture;
  // The live score readout is off the exercise page on every breakpoint —
  // desktop overlay, portrait stat bar and the landscape camera-card chip all
  // hang off this one flag. A display switch, not a teardown: the score still
  // arrives on the socket, drives `scoreStyles`, and the rest/end summaries
  // still report form quality. `display.showsQuality` stays the answer to
  // whether this run *could* score (see runtime-display-mode) — it just no
  // longer decides whether the number is shown while exercising.
  const isQualityReadoutAvailable = false;
  // The exercise clock's owner depends on `type`, so the freeze flags
  // (isUserTimerFrozen / isEftFrozen) are derived below, once exerciseTypeValue
  // is known.
  //
  // The backend's `isIdlePerformance` frame flag ("no pose and no rep for 2
  // minutes") is deliberately not read at all any more: it only ever drove the
  // idle countdown modal, which has been removed along with its timer-freezing.
  // A set that gets no pose data now simply runs its countdown to the end.
  //
  // `rectangleColor` off the live socket payload (shapeColor() from
  // useWebSocketConnection). Whenever the backend sends one it wins for BOTH the
  // framing border and the skeleton, so the two always agree; the local
  // person-missing color below is only the fallback for when the backend sends
  // nothing (flag `none`). A green payload is discarded here rather than in
  // resolveRectangleColor, so the mapping itself stays a faithful reading of
  // what the backend sent.
  const rawSocketRectangleColor = resolveRectangleColor(shapeColor?.());
  const socketRectangleColor = isGreenRectangleColor(rawSocketRectangleColor)
    ? null
    : rawSocketRectangleColor;
  const showBorder = isCameraFeedVisible && (isPersonMissing || Boolean(socketRectangleColor));
  // Reached only when showBorder is true, so with no socket color the reason is
  // always a missing person — hence red as the sole fallback.
  const borderShapeColor = () => socketRectangleColor ?? rectangleFlag.red;
  const skeletonColor =
    socketRectangleColor ??
    (isPersonMissing
      ? '#EF4444' // red — matches the red border
      : '#FFFFFF'); // border hidden → white skeleton
  // `type` (REP_BASED / TIMED_REPS / TIMED_HOLD) is stated on the intro payload
  // once per exercise, so it is mirrored to localStorage by the effect below and
  // read back here — a remount or a reconnect mid-set must not lose it. The
  // prescribed value is the last resort. The retired `exercise_mode` /
  // "Fixed Reps" / "Fixed Time" vocabulary is gone.
  const exerciseTypeValue =
    resolveExerciseType(state.introResult) ??
    resolveExerciseType(IntroResult) ??
    readStoredExerciseType() ??
    resolveExerciseType(initialConfig?.settings);

  // Portrait mobile renders the counter/quality/timer as a band between the two
  // video halves instead of as overlay pills, so the page has to derive what
  // that band shows. Same rules the desktop overlay uses — both read them off
  // resolveExerciseReadoutFlags.
  const { isTimedHold, isTimedReps, isRepBased, showsCounter, showsTimer } =
    resolveExerciseReadoutFlags(exerciseTypeValue, { isOffline: display.isOffline });
  // TIMED_REPS has an open counter: time ends the set, not a target rep count.
  // TIMED_HOLD has no reps. Only REP_BASED renders completed / total reps.
  const totalReps =
    isTimedHold || isTimedReps
      ? undefined
      : Number(state.introResult?.reps) || Number(initialConfig?.settings?.reps) || undefined;

  // The set's own budget, stated on the intro payload as `set_eta_seconds`
  // (the retired `eta` is gone). The FE never hardcodes a countdown length —
  // every type, REP_BASED included, counts down against this value as-is.
  const setEtaSeconds =
    Number(state.introResult?.set_eta_seconds) ||
    Number(initialConfig?.settings?.set_eta_seconds) ||
    60;
  const totalTime = setEtaSeconds;

  // Whether the score/FPS debug pill (ScoreAndFpsOverlay, fixed top-4) is
  // actually on screen right now. The TIMED_REPS countdown chip drops below it
  // when it's showing to avoid overlapping, and sits flush at the top otherwise.
  // Gated on the environment flag, not the per-device `score` overlay: that one
  // starts off on phones and nothing in the UI turns it on, so the device FPS
  // could never be checked on a phone.
  const hasFpsOverlay =
    FEATURES.DEBUG_OVERLAYS && inPerformance && (currentFps !== null || state.frontendFps !== null);

  // The hold set's wall-clock ceiling (`set_ceiling_seconds`, twice the eta), read
  // the same way the type is: intro payload first, the localStorage mirror as the
  // remount/reconnect fallback. The backend sends it on TIMED_HOLD only, so every
  // other type resolves to null and shows nothing.
  const setCeilingSeconds =
    resolveSetCeilingSeconds(state.introResult) ??
    resolveSetCeilingSeconds(IntroResult) ??
    readStoredSetCeilingSeconds();

  // The wall reading banked when this set started — the frontend's copy of the
  // backend's `wall_eft_offset`, so the ceiling counts per set rather than from
  // the socket's own zero. Written by the userTimer anchor effect and by
  // resetUserTimer (restart/skip), both further down.
  const setCeilingAnchorRef = useRef(null);
  // Type and the ceiling itself are the only gates. Deliberately NOT the
  // online/offline split: the backend states a ceiling for every TIMED_HOLD
  // whatever the execution mode, and gating on the camera hid the readout on
  // exactly the sessions it was easiest to test on (camera off, uploaded video).
  const showSetCeilingCountdown =
    isTimedHold && inPerformance && canStartUserTimer && Boolean(setCeilingSeconds);
  const setCeilingTimerValue = showSetCeilingCountdown
    ? resolveRemainingSetCeilingTime({
        time: state.time,
        ceilingSeconds: setCeilingSeconds,
        anchorMs: setCeilingAnchorRef.current,
      })
    : null;

  // REP_BASED starts every set at 0 completed reps — showing "0%" there reads as
  // an actual (bad) score rather than "nothing scored yet", since the first
  // score only exists once a rep has actually been evaluated. Every other type
  // has a score that means something from the first frame.
  const hasQualityScore = !(isRepBased && (displayCounter ?? 0) === 0);

  // Who owns userTimer + eft-time for this exercise — see resolveTimerFreezePolicy.
  //
  //  TIMED_HOLD (a single_rep movement)
  //     online  — the backend owns the clock: it stops only when the latest
  //               frame reports `holdTimerPaused: true` from BOTH sources (its
  //               own data object and the matcher's conditionsState). The two
  //               continue from where they stopped once both report running
  //               again.
  //     offline — no pose is being scored, so nothing can legitimately freeze
  //               the clock: the countdown is pure time and runs to the end. The
  //               guide says the backend never raises the flag without motion
  //               capture, and gating on it here means a stale frame from just
  //               before a camera drop cannot leave the timer frozen for the
  //               rest of the set.
  //     Stage 2 (IN_PERFORMANCE) only: the flag describes what the user is doing
  //     inside a working set, so the intro/rest/post countdowns keep running
  //     regardless, and a frame arriving on the way into or out of a set cannot
  //     strand them.
  //
  //  REP_BASED and TIMED_REPS — follow the same backend holdTimerPaused gate as
  //     TIMED_HOLD above, so userTimer and eft-time stop and resume together.
  //     REP_BASED renders no countdown at all (showsTimer is false above), but
  //     eft-time still runs under this gate — the backend needs it.
  //
  //  Offline (NO_MOTION_CAPTURE, or no live camera) — nothing above applies: the
  //     policy resolves to `never`, so the countdown shows and runs to the end on
  //     its own whatever the type. Hence no isOnline check below: the policy
  //     already carries it.
  const timerFreezePolicy = resolveTimerFreezePolicy(exerciseTypeValue, {
    isOffline: display.isOffline,
  });
  const isBackendTimerRunning = isTimedReps ? isTimedRepsTimerRunning : isHoldTimerRunning;
  const isEftFrozen =
    timerFreezePolicy === TIMER_FREEZE_POLICY.backend && inPerformance && !isBackendTimerRunning;
  const isUserTimerFrozen = isEftFrozen;
  // CameraDisabledInfo owns its own open state, so mirror it here: it is the one
  // wall-clock pause owner the effects below cannot otherwise see.
  const [isCameraInfoOpen, setIsCameraInfoOpen] = useState(false);
  // The wall clock's only gate — deliberately not the quality freeze above.
  const isSessionSuspended = resolveSessionSuspended({
    openSettingModel: state.openSettingModel,
    isPauseStage: isPaseStage,
    isCameraInfoOpen,
  });
  const isMobilePerformanceActive = isStackedMobileLayout && inPerformance && canStartUserTimer;
  // Offline the pill shows the prescribed target instead of a live count, so it
  // stays on screen either way — only its content changes.
  const showMobileCounter = isMobilePerformanceActive && showsCounter;
  const showMobileTimer = isMobilePerformanceActive && showsTimer;
  const mobileTimerValue = showMobileTimer
    ? resolveRemainingExerciseTime({ time: state.time, totalTimeSeconds: totalTime })
    : null;
  // Frame 25807 (hold movements) shows the first pill with a stopwatch glyph
  // and an elapsed count instead of the repeat glyph and a rep count.
  const isMobileHoldMode = isTimedHold;

  // Landscape phone (Figma node 1527-28084/28044/28002): split across the two
  // cards, not grouped in one corner like desktop. Quality always sits on the
  // camera card; the timer joins it there only when a counter is also showing
  // (TIMED_REPS — the counter then takes the video card instead), otherwise the
  // timer is the video card's own top-left readout.
  const isLandscapePerformanceActive = isLandscapeMobile && inPerformance && canStartUserTimer;
  const showLandscapeCounter = isLandscapePerformanceActive && showsCounter;
  const showLandscapeTimer = isLandscapePerformanceActive && showsTimer;
  const showLandscapeQuality = isLandscapePerformanceActive && isQualityReadoutAvailable;
  const landscapeTimerValue = showLandscapeTimer
    ? resolveRemainingExerciseTime({ time: state.time, totalTimeSeconds: totalTime })
    : null;
  const cameraShowsLandscapeTimer = showLandscapeTimer && showLandscapeCounter;
  const videoShowsLandscapeTimer = showLandscapeTimer && !showLandscapeCounter;

  // useRecordingVideo({
  //   videoRef,
  //   isCameraActive: state.cameraIsActive,
  //   shouldRecord: shouldRecordVideo,
  //   // Rest is between sets of the SAME exercise — pause, don't end the clip.
  //   // isUpComingStage (next exercise) ends it instead, via shouldFinalizeRecording.
  //   isPaused: isRestStage,
  //   videoRotation: state.videoRotation,
  //   movementName: exerciseName,
  //   sessionId: initialConfig?.sessionId,
  //   recordingDurationSourceMs: currentUserTimerMs,
  //   setRecordingDate,
  //   shouldFinalizeRecording: isUpComingStage || isPostStage,
  // });

  // useEffect(() => {
  //   removeFromLocalStorageInPerformance();
  //   localStorage.removeItem('latestRecordingStartedAt');
  //   clearLatestRecordingPayload().catch(error => {
  //     console.error('Failed to clear previous recording before exercise starts:', error);
  //   });
  // }, []);

  useEffect(() => {
    if (IntroResult) {
      setIntroResult(IntroResult);
      localStorage.setItem('video-url', IntroResult.referenceVideoUrl || '');
      // Mirror (or clear) the movement's readout instruction as its intro lands,
      // so the next movement never inherits the previous one's value.
      persistExerciseType(resolveExerciseType(IntroResult));
      // Same mirror, same clearing rule: a REP_BASED/TIMED_REPS intro carries no
      // ceiling, so this wipes the previous hold's value rather than leaving it
      // to be read back on the next exercise.
      const ceiling = resolveSetCeilingSeconds(IntroResult);
      persistSetCeilingSeconds(ceiling);
      // The ceiling readout has no other source, so a hold that arrives without
      // one silently shows nothing. Say why: it means this backend predates
      // `set_ceiling_seconds` on the intro payload (or the exercise carries no
      // eta), not that the readout is broken.
      if (ENV.isDebug && !ceiling && resolveExerciseType(IntroResult) === exerciseTypes.timedHold) {
        console.warn(
          'No `set_ceiling_seconds` on this TIMED_HOLD intro payload — the set ceiling countdown stays hidden.',
          IntroResult
        );
      }
    }
  }, [IntroResult]);

  // Keep the pause modal's mode toggle in sync with the mode the backend reports
  // as actually in effect (`execution_mode` on every frame). Guarded on a
  // recognised value so frames that omit it don't reset the toggle, and only
  // dispatched on an actual change to avoid reducer churn every message.
  useEffect(() => {
    const incomingMode = resolveExecutionMode({ execution_mode: socketExecutionMode });
    if (incomingMode && incomingMode !== state.executionMode) {
      dispatch({ type: exercisesStats.executionMode, payload: incomingMode });
    }
  }, [socketExecutionMode]);

  useEffect(() => {
    return () => {
      if (state.uploadedVideoUrl) {
        URL.revokeObjectURL(state.uploadedVideoUrl);
      }
    };
  }, [state.uploadedVideoUrl]);

  useEffect(() => {
    if (!state.recordingDate) {
      return;
    }

    localStorage.setItem('latestRecordingStartedAt', String(state.recordingDate));
  }, [state.recordingDate]);

  // "Restart exercise" arms skipNextPreStartCountdownRef (declared above, next to
  // preStartCountdownValue): that flow closes the pause modal and drops the user
  // straight back into the set, so the get-ready countdown is skipped for that one
  // re-entry. Skipping it is the point — the countdown is what pauses frame sending
  // (isFrameSendingPaused), and a restart should only rewind the set, never
  // interrupt the stream. Consumed here on the first re-entry after the restart; a
  // later set entry gets its countdown as usual.
  useEffect(() => {
    if (!shouldShowPreStartCountdown) {
      setPreStartCountdown(null);
      setIsPreStartCountdownComplete(false);
      return undefined;
    }

    if (skipNextPreStartCountdownRef.current) {
      skipNextPreStartCountdownRef.current = false;
      setPreStartCountdown(null);
      setIsPreStartCountdownComplete(true);
      return undefined;
    }

    setIsPreStartCountdownComplete(false);
    setPreStartCountdown(3);

    // Sound itself plays from AudioSection (getReadyCountdownValue prop),
    // using the same Web Audio buffer mechanism as the counter tick — a bare
    // `new Audio(...).play()` here isn't unlocked by the user-gesture flow
    // AudioSection owns, so it silently failed on gated browsers.

    const startedAt = performance.now();
    const intervalId = window.setInterval(() => {
      // Derive the remaining count from real elapsed time rather than
      // decrementing by 1 per fire, so a late tick under main-thread jank
      // doesn't make the countdown run slower than real seconds.
      const remaining = 3 - Math.floor((performance.now() - startedAt) / 1000);

      if (remaining <= 0) {
        window.clearInterval(intervalId);
        setPreStartCountdown(null);
        setIsPreStartCountdownComplete(true);
        return;
      }

      setPreStartCountdown(remaining);
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
    // Deliberately NOT state.cameraIsActive: a camera that's flapping (a broken
    // device stuck in getUserMedia's fail -> retry -> fail loop) toggles that
    // value repeatedly, and it used to sit in this array — every flap restarted
    // the countdown from 3, so on an offline/no-camera session it could never
    // reach 0. isFrameSendingPaused (below) stays true until this countdown
    // completes, so that dead loop was also a dead loop on ever sending a frame
    // again after intro.
  }, [shouldShowPreStartCountdown, state.mediaSourceType, state.uploadedVideoUrl]);

  // Every "3, 2, 1": the user is still getting into position, so those seconds
  // are neither effective time nor session time. eft-time and the wall clock are
  // both held for the length of the countdown and resumed by the resume effects
  // below once it lands — not just the first one. A countdown that follows
  // REST/UPCOMING (not the settings/pause modal) has no other owner pausing
  // them, so without this they kept running straight through the get-ready beat
  // on every set after the first.
  //
  // Held, not banked: resumeTimer folds the pause into `totalPauseTime`
  // (utilis/helpers), so elapsed skips it — the backend never receives a jump
  // covering the countdown, and no frame carries it in the meantime either
  // (isFrameSendingPaused stops sending while the countdown is on screen).
  //
  // pauseTimer no-ops if the key is already paused, so this is harmless when
  // the settings/pause modal's own effect got here first.
  useEffect(() => {
    if (preStartCountdownValue === null) {
      return;
    }

    pauseTimer({ key: 'eft-time', time: state.time });
    pauseTimer({ key: SESSION_WALL_TIMER_KEY, time: state.time });
  }, [preStartCountdownValue]);

  useRedLandMarks({ responseLandMarks });

  // getElapsedRunningTime() (not a manual `performance.now() - startRef` diff)
  // so that pausing/resuming this timer (settings modal open/close) excludes
  // the paused duration instead of jumping state.time forward by however long
  // it was paused once resumed. timerRef exists because the callback needs to
  // read the timer instance useTimer itself hasn't returned yet at this point.
  const timerRef = useRef(null);
  const timer = useTimer({ delay: 30, startImmediately: false }, () => {
    connectionStatus === 'Open' && setTime(timerRef.current?.getElapsedRunningTime() ?? 0);
  });
  timerRef.current = timer;

  // Left `startImmediately`, the precision timer ticked from mount, but its
  // elapsed time only reached `state.time` once `connectionStatus === 'Open'`
  // (above) — so whatever ran up while the camera/model/socket were still
  // connecting (seconds, not 30ms) landed as the very first committed value,
  // and eft-time's onOpen baseline (state.time, still its pre-connect value)
  // was already behind it. Starting the timer fresh right as the socket opens
  // keeps both anchored to the same zero, so the first real tick after Open is
  // a normal 30ms step. Guarded to fire once: later reconnects (network drop)
  // reuse the same socket and must not rewind the master clock other timers
  // are already anchored against.
  const hasStartedMasterClockRef = useRef(false);
  useEffect(() => {
    if (connectionStatus === 'Open' && !hasStartedMasterClockRef.current) {
      hasStartedMasterClockRef.current = true;
      timer.start();
      setTime(0);
    }
  }, [connectionStatus]);

  useEffect(() => {
    if (connectionStatus === 'Open' && isPoseModelReady) {
      if (!state.isCConfigure) {
        addTimer({ key: 'timeLeft', value: state.time / 1000 });
      }
      setIsCConfigure(true);
    }
  }, [state.isCConfigure, isPoseModelReady]);

  // Anchor userTimer once per genuine performance segment (backend flips to
  // IN_PERFORMANCE + the local get-ready countdown finishes), not on every
  // canStartUserTimer flip — that flag also toggles when the settings/pause
  // modal opens or closes (it re-runs the pre-start countdown), which used to
  // re-call addTimer and reset elapsed execution time instead of continuing
  // it after resume. performanceStartedRef re-arms once inPerformance goes
  // false, so the next set still gets a fresh start.
  const performanceStartedRef = useRef(false);
  useEffect(() => {
    const shouldStart = connectionStatus === 'Open' && inPerformance && canStartUserTimer;
    if (shouldStart && !performanceStartedRef.current) {
      addTimer({ key: 'userTimer', value: state.time });
      // Bank the wall clock's reading for the ceiling countdown at the same
      // moment — the backend anchors its own `wall_eft_offset` on this set's
      // first execution frame, which is the frame that follows this.
      setCeilingAnchorRef.current = getTimer({ key: SESSION_WALL_TIMER_KEY, timer: state.time });
      performanceStartedRef.current = true;
    } else if (!inPerformance) {
      performanceStartedRef.current = false;
      // Cleared between sets so the rest screen's next set starts from the full
      // ceiling instead of inheriting the previous set's anchor.
      setCeilingAnchorRef.current = null;
    }
  }, [connectionStatus, inPerformance, canStartUserTimer]);

  useEffect(() => {
    if (!inPerformance && timers['timeLeft']) {
      // use for get time for rest to show
      pauseTimer({ key: 'timeLeft', time: state.time });
    }
  }, [inPerformance]);

  useEffect(() => {
    if (isUpComingStage) {
      setIsCConfigure(false);
    }
  }, [isUpComingStage]);

  useEffect(() => {
    if (inPerformance && state.isPreStartCountdownComplete) {
      setIsRecordingActive(true);
    }
  }, [inPerformance, state.isPreStartCountdownComplete]);

  // isUpComingStage (comingup, between exercises) ends this exercise's clip
  // same as isPostStage (true session end) — both are a real finalize, and
  // the effect below re-arms isRecordingActive on the next exercise's own
  // inPerformance + countdown-complete, starting a fresh clip for it.
  useEffect(() => {
    if (isUpComingStage || isPostStage) {
      setIsRecordingActive(false);
    }
  }, [isUpComingStage, isPostStage]);

  // Pause treatment for the settings/pause modal and the backend's own pause
  // stage — userTimer/eft-time must stop accumulating the instant either one
  // goes up, so effective time never counts a paused exercise.
  useEffect(() => {
    if (isSessionSuspended) {
      pauseTimer({ key: 'userTimer', time: state.time });
      pauseTimer({ key: 'eft-time', time: state.time });
      pauseTimer({ key: SESSION_WALL_TIMER_KEY, time: state.time });
      return;
    }

    // The wall clock waits for the get-ready countdown, same gate as eft-time
    // below — so the beat costs the same on both paths into a set (fresh entry,
    // and re-entry after the modal). This is still its ONLY resume: the freeze
    // effect further down never touches it, which is the whole point of it.
    if (!inPerformance || canStartUserTimer) {
      resumeTimer({ key: SESSION_WALL_TIMER_KEY, time: state.time });
    }

    // Outside a performance segment (e.g. on the intro or rest screen) there is
    // no pre-start "3,2,1" countdown to re-arm canStartUserTimer, so the
    // countdown-gated resume effect below never fires and eft-time would stay
    // frozen. Resume it here so effective time keeps flowing to the backend.
    // During performance that effect handles the resume, so the user isn't timed
    // during the get-ready beat.
    if ((!inPerformance || canStartUserTimer) && !isEftFrozen) {
      resumeTimer({ key: 'eft-time', time: state.time });
    }

    // Leaving the backend pause stage mid-performance doesn't re-run the
    // get-ready countdown either, so userTimer is resumed here rather than left
    // to that effect.
    if (inPerformance && canStartUserTimer && !isUserTimerFrozen) {
      resumeTimer({ key: 'userTimer', time: state.time });
    }
  }, [isSessionSuspended, inPerformance, canStartUserTimer, isEftFrozen, isUserTimerFrozen]);

  // Resume only once the post-close "3, 2, 1" get-ready countdown finishes
  // (canStartUserTimer), not the instant the modal closes — closing it drops
  // straight into PreStartCountdown, and the user shouldn't be timed/scored
  // during that beat. resumeTimer no-ops if the key isn't currently paused,
  // so this is harmless on every other canStartUserTimer flip (e.g. after
  // the intro) that isn't following a settings-modal pause.
  useEffect(() => {
    if (!canStartUserTimer) {
      return;
    }

    // The wall clock is deliberately absent: the effect above is its one owner
    // and it fires on this very same canStartUserTimer flip, so it resumes the
    // wall clock there — but only when the session isn't suspended.
    if (!isEftFrozen) {
      resumeTimer({ key: 'eft-time', time: state.time });
    }

    if (inPerformance && !isUserTimerFrozen) {
      resumeTimer({ key: 'userTimer', time: state.time });
    }
  }, [canStartUserTimer]);

  // The freeze itself — the backend's `holdTimerPaused` pair holds both keys,
  // the same gate for every exercise type.
  // The wall clock is never held here — that is the whole point of it, and it is
  // what lets the backend cap a set this freeze would otherwise stall forever.
  // Neither are the intro/rest countdowns. When a freeze clears, the key
  // continues from where it stopped rather than restarting.
  //
  // Declared after the resume effects above on purpose: effects run in
  // declaration order, so on a render where the backend freezes at the same
  // moment something else resumes, the freeze is what lands.
  useEffect(() => {
    if (isUserTimerFrozen) {
      pauseTimer({ key: 'userTimer', time: state.time });
    }
    if (isEftFrozen) {
      pauseTimer({ key: 'eft-time', time: state.time });
    }

    // Whatever is still running may nevertheless be paused by another owner —
    // the settings/pause modal or the get-ready countdown. Resuming here would
    // override them, so bail and let whichever effect owns that pause do the
    // resume when its own condition clears.
    if (state.openSettingModel || isPaseStage || !canStartUserTimer) {
      return;
    }

    if (inPerformance && !isUserTimerFrozen) {
      resumeTimer({ key: 'userTimer', time: state.time });
    }
    if (!isEftFrozen) {
      resumeTimer({ key: 'eft-time', time: state.time });
    }
  }, [
    isUserTimerFrozen,
    isEftFrozen,
    state.openSettingModel,
    isPaseStage,
    canStartUserTimer,
    inPerformance,
  ]);

  // Leaving the tab/window (switching apps, changing tab, or minimizing) opens
  // the settings/pause modal automatically, so the exercise never keeps running
  // unattended while the user can't see it. Same side effects as tapping the
  // menu icon (BordersAndSettingIcon): stop sending, pause the local timer, and
  // tell the backend to pause. Read openSettingModel from a ref so this effect
  // can subscribe once (stable deps) instead of re-subscribing — and still see
  // the latest value inside the handler without a stale closure.
  const openSettingModelRef = useRef(state.openSettingModel);
  openSettingModelRef.current = state.openSettingModel;
  useEffect(() => {
    const openSettingsOnBlur = source => {
      // Already up — don't double-pause.
      if (openSettingModelRef.current) {
        return;
      }
      dispatch({ type: exercisesStats.openSettingModel, payload: true });
      dispatch({ type: exercisesStats.stopSend, payload: false });
      timer?.pause?.();
      // The backend pause only makes sense on a live socket; opening the modal
      // itself must happen regardless of connection state.
      if (connectionStatus === 'Open') {
        sendJsonMessage?.({ pause: { action: SettingMode.pause } });
      }
    };

    const handleBlur = () => openSettingsOnBlur('window-blur');
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        openSettingsOnBlur('visibility-hidden');
      }
    };

    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [connectionStatus, timer, sendJsonMessage]);

  // The pause modal only ever closed on the user clicking Resume — nothing
  // watched the backend's own stage. If the set finished (stage flips to
  // EXERCISES_DONE) while the modal happened to be open, it just sat there at
  // z-[9999], on top of and blocking the "coming up" overlay underneath it
  // (RestUiSection, z-[100]) forever. Same close actions as clicking Resume
  // (see handleCloseSettingModal in exercise-setting-modal), just triggered by
  // the stage instead of a click.
  useEffect(() => {
    if (isUpComingStage && state.openSettingModel) {
      dispatch({ type: exercisesStats.stopSend, payload: true });
      dispatch({ type: exercisesStats.openSettingModel, payload: false });
      timer?.resume?.();
    }
  }, [isUpComingStage, state.openSettingModel, dispatch, timer]);

  ///start project for send and detect media pipe
  usePredict({
    canvasForObjectRef,
    initialConfig,
    postPerformance,
    responseLandMarks,
    sendJsonMessage: sendJsonMessage,
    state,
    videoRef,
    canvasRef,
    setDrawOnImage,
    audioSectionRef,
    time: state.time,
    voiceResult,
    isRestStage,
    isUpComingStage,
    isFrameSendingPaused,
    isDetectionPaused,
    cameraAvailable,
    // Same flag that gates whether the loading screen below is still being
    // shown — the detect/send loop must not start until that screen is gone.
    modelReady: isPoseModelReady,
    canRunDetection: state.cameraIsActive
      ? state.isPreStartCountdownComplete || isRestStage || isUpComingStage || isIntroStage
      : state.stopSend,
    onFrontendFpsChange: setFrontendFps,
    skeletonCanvasRef,
    skeletonColor,
    onPersonPresenceChange: setPersonInFrame,
    isStackedMobile: isStackedMobileLayout,
  });

  useEffect(() => {
    if (voiceResult) {
      setSoundPost(voiceResult);
    }
  }, [voiceResult]);

  // End-of-session feedback lives on the post-performance screen now, not here
  // — the exercise page must never interrupt a session with it.
  //
  // "Complete later" with no exitUrl has no external app to hand the user back
  // to. Rather than dropping them one entry back in history, send them to
  // post-performance, which is where the feedback popup now asks its questions.
  const handleExitWithoutRedirect = () => {
    closeCameraAfterNavigate();
    navigate('/post-performance', { replace: true });
  };

  usePostPerformance({
    isPostStage,
    closeCameraAfterNavigate,
    completeUrl,
  });

  const handleRestart = () => {
    dispatch({ type: exercisesStats.stopSend, payload: true });
    setReconnect(true);
    dispatch({
      type: exercisesStats.currentSet,
      payload: 0,
    });
    deleteTimer({ key: 'eft-time' });
    deleteTimer({ key: SESSION_WALL_TIMER_KEY });
    deleteTimer({ key: 'userTimer' });
    setIsCConfigure(false);
    setIsRecordingActive(false);
    timer.stop();
    timer.start();
    setTime(0);
  };

  // "Restart exercise" from the pause menu keeps the same socket connection
  // (unlike handleRestart above, which reconnects entirely) — it just needs
  // the user/eta timer re-anchored to the current clock so the countdown
  // starts fresh instead of resuming from its pre-restart elapsed value.
  //
  // `skipCountdown` defaults to true for an actual restart: straight back into
  // the same set, no "3, 2, 1" on the way in, so frame sending never stops
  // (see skipNextPreStartCountdownRef). Skipping to the NEXT exercise reuses
  // this same re-anchoring (see handleExerciseSkipped below) but must NOT
  // suppress the countdown — that exercise hasn't been performed yet and
  // deserves the same "3, 2, 1" get-ready beat any fresh exercise entry gets.
  const resetUserTimer = ({ skipCountdown = true } = {}) => {
    performanceStartedRef.current = false;
    skipNextPreStartCountdownRef.current = skipCountdown;
    deleteTimer({ key: 'userTimer' });
    addTimer({ key: 'userTimer', value: state.time });
    // Re-anchor eft-time too. Restart is confirmed from the settings modal,
    // which paused eft-time — and since this restart reuses the socket, the
    // onOpen handler that would normally re-add it never fires. Left as-is it
    // stays paused, so getTimer keeps returning the same frozen value and the
    // backend receives a constant eft forever. Delete + re-add gives a fresh,
    // un-paused timer counting from the restart moment (same as handleRestart).
    deleteTimer({ key: 'eft-time' });
    addTimer({ key: 'eft-time', value: state.time });
    // The wall clock is deliberately NOT re-anchored: it is anchored to the socket,
    // not to an attempt, so it stays monotonic for the backend. The backend
    // re-anchors its own per-set offset when it rebuilds the set after this swap,
    // and the ceiling readout re-anchors here to match — the effect above cannot,
    // since a restart flips none of its deps (canStartUserTimer stays true when
    // the countdown is skipped).
    setCeilingAnchorRef.current = getTimer({ key: SESSION_WALL_TIMER_KEY, timer: state.time });
  };

  // "Skip exercise" keeps the backend in IN_PERFORMANCE (only exerciseId
  // changes) — see the skip-tracking effect in setting-modal/index.jsx, which
  // calls this once the backend confirms it actually moved to the next
  // exercise. userTimer/eft-time need the same re-anchoring a restart gets,
  // but the countdown must run: this is a new exercise, not a resumed one.
  const handleExerciseSkipped = () => resetUserTimer({ skipCountdown: false });

  // The "how to allow the camera" info modal (top bar, offline mode) should
  // freeze the whole exercise while it's open — every active timer plus the
  // master precision clock (state.time) — and continue exactly where it left
  // off on close. We record which keys/clock WE paused so closing only resumes
  // those, instead of un-pausing a timer another modal (settings/idle) had
  // already frozen.
  const cameraInfoPausedKeysRef = useRef([]);
  const cameraInfoPausedPrecisionRef = useRef(false);

  const handleCameraInfoOpen = () => {
    // Mirrored into state as well as paused imperatively below: the wall clock's
    // declarative owner has to see this modal, or it would resume it underneath us.
    setIsCameraInfoOpen(true);
    const pausedKeys = [];
    Object.keys(timers).forEach(key => {
      if (timers[key] && !timers[key].isPaused) {
        pauseTimer({ key, time: state.time });
        pausedKeys.push(key);
      }
    });
    cameraInfoPausedKeysRef.current = pausedKeys;

    if (timer && !timer.isPaused?.()) {
      timer.pause?.();
      cameraInfoPausedPrecisionRef.current = true;
    }
  };

  const handleCameraInfoClose = () => {
    setIsCameraInfoOpen(false);
    cameraInfoPausedKeysRef.current.forEach(key => {
      resumeTimer({ key, time: state.time });
    });
    cameraInfoPausedKeysRef.current = [];

    if (cameraInfoPausedPrecisionRef.current) {
      timer?.resume?.();
      cameraInfoPausedPrecisionRef.current = false;
    }
  };

  // Settings modal must stay available on the intro screen too — clicking the
  // menu icon there only dispatches openSettingModel/pauses the socket, it
  // doesn't skip the intro, so the modal has to render in both branches below.
  const settingModalNode = state.openSettingModel && (
    <ExerciseSettingModal
      cleanupStream={closeCameraAfterNavigate}
      handleRemoveFromLocalForage={removeFromLocalStorageInPerformance}
      handleRestartStates={handleRestart}
      onRestartExercise={resetUserTimer}
      onExerciseSkipped={handleExerciseSkipped}
      handlelReConnect={handlelReConnect}
      inPerformance={inPerformance}
      openSettingmodal={state.openSettingModel}
      parseTimer={parseTimer}
      state={state}
      setReconnect={setReconnect}
      dispatch={dispatch}
      timer={timer}
      time={state.time}
      sendJsonMessage={sendJsonMessage}
      // The modal renders the exercise list, so it gets the latched payload —
      // the raw flag is only used for timer pausing above, where a value that
      // survives past the backend's own pause window would be wrong.
      isPaseStage={pauseStageData}
      currentExerciseOrder={currentPauseStageOrder}
      exitUrl={exitUrl}
      onExitWithoutRedirect={handleExitWithoutRedirect}
      setTime={setTime}
      sessionId={initialConfig?.sessionId}
      setIsCConfigure={setIsCConfigure}
      setToggleMirror={setToggleMirror}
      toggleMirror={state.toggleMirror}
    />
  );

  // Only reached when EVERY rung of the runtime fallback ladder failed
  // (Worker+GPU -> Worker+CPU -> main-thread+GPU -> main-thread+CPU) — see
  // zustand/pose-worker-store.js. That means a genuine hard failure (e.g. the
  // model/WASM assets couldn't be fetched, or the device can't run MediaPipe at
  // all), not merely an incompatible delegate. Without this branch the user
  // would sit on the loading screen forever, so surface it with a retry (which
  // re-walks the whole ladder) instead of leaving them stuck.
  if (poseModelError) {
    return (
      <div tw="flex flex-col justify-center items-center w-screen h-screen gap-4 bg-white px-6 text-center">
        <p tw="text-xl font-bold text-[#2e2e2e]">{t('page.modelErrorTitle')}</p>
        <p tw="text-sm text-gray-500 max-w-[420px]">{t('page.modelErrorDescription')}</p>
        {poseModelError && (
          <p tw="text-xs text-red-500 max-w-[420px] break-words font-mono">
            {String(poseModelError)}
          </p>
        )}
        <div tw="flex items-center gap-3 mt-2">
          <button
            type="button"
            onClick={() => retryPoseModel?.()}
            tw="rounded-[8px] bg-[#8DA12B] px-5 py-2.5 text-sm font-semibold text-white"
          >
            {t('page.tryAgain')}
          </button>
          <button
            type="button"
            onClick={() => navigate('/home', { replace: true })}
            tw="rounded-[8px] border border-[#D9DFE4] px-5 py-2.5 text-sm font-semibold text-[#2e2e2e]"
          >
            {t('page.backToHome')}
          </button>
        </div>
      </div>
    );
  }

  // The pose model/worker takes a moment to load on first entry — hold off on
  // connecting the socket (see the `enabled` flag above) and show a loading
  // modal instead of the exercise UI until it's ready.
  if (!isPoseModelReady) {
    return <AvocatoLoading text={t('page.loadingExercises')} />;
  }

  // Stage 5 (POST_PERFORMANCE) means the backend is done and usePostPerformance
  // is about to navigate away (post-performance, or completeUrl if the session
  // came with one) from its own effect below. On a weak connection the
  // "coming up" overlay can be mid-stuck when this arrives, so without this
  // guard the exercise UI flashes back for a frame before that navigation
  // actually fires. Covering the screen the instant the stage lands — this
  // same render, before the effect runs — replaces that flash with a plain
  // loading screen instead.
  if (isPostStage) {
    return (
      <div tw="flex flex-col justify-center items-center w-screen h-screen gap-4 bg-white">
        <LoadingElement width={60} height={60} />
        <p tw="text-xl font-semibold text-[#6d737b]">{t('page.finishingSession')}</p>
      </div>
    );
  }

  // Single camera owner, built once and reused by both the intro branch and
  // the main exercise return below. Previously each branch declared its own
  // <WebcamAndSkelatonSide>, so React saw two different element trees across
  // the showIntro toggle and fully unmounted/remounted the camera (new
  // getUserMedia call) on every intro -> exercise transition — on iOS this
  // rapid stream teardown/reacquire can leave the camera permanently dead.
  // Reusing the same element reference keeps it mounted across that toggle.
  const webcamSide = (
    <WebcamAndSkelatonSide
      setDrawOnImage={setDrawOnImage}
      canvasElement={canvasElement}
      inPerformance={inPerformance}
      postPerformance={!!postData}
      shapeBorderStyle={shapeColor}
      canvasForObjectDetectionRef={canvasForObjectDetectionRef}
      videoRef={videoRef}
      totalTime={totalTime}
      isRestStage={isRestStage}
      sectorCanvasRef={SectorCanvasRef}
      isRightSide={
        typeof state.toggleMirror === 'boolean'
          ? state.toggleMirror
          : initialConfig?.settings?.side === 'Right'
      }
      isBluer={data?.response_data?.blur_camera}
      canvasForObjectRef={canvasForObjectRef}
      dispatch={dispatch}
      canvasRef={canvasRef}
      audioSectionRef={audioSectionRef}
      exerciseType={exerciseTypeValue}
      isCameraTranslated={state.isCameraTranslated}
      setIsCameraTranslated={setIsCameraTranslated}
      mediaSourceType={state.mediaSourceType}
      uploadedVideoUrl={state.uploadedVideoUrl}
      videoRotation={state.videoRotation}
      isStackedMobile={isStackedMobileLayout || isLandscapeMobile}
      skeletonCanvasRef={skeletonCanvasRef}
      // Desktop only — no UI exposes this as a choice (same as camera view
      // mode, see camera-view-mode-store.js), so it's tied straight to the
      // device rather than a togglable/persisted preference.
      showSkeleton={!isPortraitMobile && !isLandscapeMobile}
    />
  );
  const cameraPortal = cameraPortalContainer
    ? createPortal(webcamSide, cameraPortalContainer, 'exercise-camera')
    : null;

  // currentSet (the reset key for showIntro) increments as soon as rest starts,
  // before the next set's performance actually begins — so only let showIntro
  // take over the screen once we're clear of rest/upcoming stage, otherwise it
  // hijacks the rest overlay the instant the set counter changes.
  const showIntroOverlay = showIntro && !isRestStage && !isUpComingStage && !inPerformance;

  // The intro is an OVERLAY inside the one return below, never its own early
  // return — ExerciseIntro is already `fixed inset-x-0 top-0 z-[800]`, so it
  // covers the runtime without replacing it.
  //
  // It used to be an early return, i.e. a completely different element tree for
  // the intro stage. The portal (cameraPortal) kept <WebcamAndSkelatonSide>
  // itself from unmounting across that swap, but the *host* element the portal
  // container is appended into lived inside the replaced tree — so on every
  // stage flip (COMING UP -> the next exercise's INTRO above all, and INTRO ->
  // IN_PERFORMANCE) React tore the old host out of the document and
  // attachCameraPortal appendChild'd the container, with the live <video> and
  // its MediaStream inside it, into a brand-new host. On iOS/WebKit a media
  // element that leaves the document has its playback/capture session torn
  // down: the MediaStreamTrack is interrupted and fires `ended`, and the
  // `track.onended` handler in webcam-and-skelaton-side then calls
  // recoverCamera() -> an imperative reacquire -> a fresh getUserMedia. That
  // fresh getUserMedia is the mid-session permission prompt.
  //
  // As an overlay nothing about the camera's position in the tree or in the DOM
  // changes between INTRO / IN_PERFORMANCE / REST / COMING UP — only what is
  // painted on top of it. AudioSection, the debug overlays and the settings
  // modal are no longer duplicated here either: they now mount once, for the
  // whole session, instead of being unmounted and rebuilt on each flip (which
  // also handed iOS a brand-new AudioContext per exercise).
  const introOverlay = showIntroOverlay ? (
    <ExerciseIntro
      videoSrc={updatedWithExtensions}
      exerciseName={exerciseName}
      countdown={countdown}
      dispatch={dispatch}
      timer={timer}
      sendJsonMessage={sendJsonMessage}
      onVideoReady={() => setVideoReady(true)}
      introResult={state.introResult}
    />
  ) : null;

  const connectionDetailsButton = showConnectionStatus ? (
    <ConnectionDetailsButton
      buttonRef={connectionDetailsButtonRef}
      severity={connectionView.severity}
      expanded={isConnectionDetailsOpen}
      onClick={toggleConnectionDetails}
    />
  ) : null;

  return (
    // Both phone layouts get the fixed, non-scrolling dvh surface — landscape
    // Safari's static 100vh is taller than the visible area too, which would push
    // the bottom of the cards under the toolbar. Desktop keeps the original
    // relative h-screen page it always had.
    <>
      {cameraPortal}
      <div
        css={[
          shellBaseStyle,
          isStackedMobileLayout || isLandscapeMobile
            ? [mobileShellStyle, mobileViewportHeight]
            : desktopShellStyle,
        ]}
      >
        <input
          ref={uploadVideoInputRef}
          type="file"
          accept="video/mp4,video/webm,video/ogg,video/quicktime"
          onChange={handleUploadedVideoChange}
          tw="hidden"
        />

        {hasFpsOverlay && (
          <ScoreAndFpsOverlay currentFps={currentFps} frontendFps={state.frontendFps} />
        )}

        {FEATURES.DEBUG_OVERLAYS && debugOverlays.holdTelemetry && (
          <HoldTelemetryOverlay telemetry={holdTelemetry} isStackedMobile={isStackedMobileLayout} />
        )}

        {FEATURES.DEBUG_OVERLAYS && debugOverlays.aspectRatio && (
          <AspectRatioOverlay
            geometry={mediaGeometry}
            mediaSourceType={state.mediaSourceType}
            isStackedMobile={isStackedMobileLayout}
          />
        )}

        {/* Landscape moves this into the reference card's top corner (see the
          landscape branch below), so the page-level copy would double it up. */}
        {!isLandscapeMobile && (
          <ExerciseTopBar
            isOfflineMode={isOfflineMode}
            isCameraRequired={!showConnectionStatus && display.isCameraMissingForMotionCapture}
            dispatch={dispatch}
            timer={timer}
            sendJsonMessage={sendJsonMessage}
            isUpComingStage={isUpComingStage}
            onCameraInfoOpen={handleCameraInfoOpen}
            onCameraInfoClose={handleCameraInfoClose}
            // Portrait phones keep the status in-flow under the camera instead
            // (see the stacked branch below), so it never sits on the person.
            statusStack={
              isStackedMobileLayout || !showConnectionStatus ? null : (
                <ConnectionStatusStack
                  status={connectionView}
                  onOpenDetails={openConnectionDetails}
                />
              )
            }
            detailsButton={connectionDetailsButton}
            detailsPanel={
              isStackedMobileLayout || !showConnectionStatus ? null : (
                <ConnectionDetailsPanel
                  status={connectionView}
                  open={isConnectionDetailsOpen}
                  onClose={closeConnectionDetails}
                />
              )
            }
          />
        )}

        {FEATURES.SET_CEILING_COUNTDOWN && (
          <SetCeilingCountdown value={setCeilingTimerValue} hasFpsOverlay={hasFpsOverlay} />
        )}

        <PreStartCountdown value={preStartCountdownValue} />

        {(() => {
          // Agent transcript when realtime is running, the legacy socket transcript
          // otherwise — so the line keeps working with the voice agent off. Built
          // once and anchored per branch below; InPerformanceText renders nothing
          // when there is no text, so the branches don't have to re-check.
          //
          // The realtime store keeps the last spoken line on screen until the next
          // one overwrites it (there's no "cleared" event, just the next response),
          // so without this it can sit there long after the agent stopped talking.
          // openAiVoiceAudible is the RMS-debounced "audibly speaking right now"
          // signal (~1s of silence before it flips), so it fades the line out soon
          // after speech actually ends without flickering between words. The legacy
          // (non-realtime) transcript has no audibility signal of its own, so it
          // keeps the old always-visible-while-present behaviour.
          const isTranscriptVisible = isRealtimeActive ? realtimeVoice.openAiVoiceAudible : true;
          // Voice agent output is an internal/QA readout, not a real-patient
          // facing feature yet — shown wherever the debug flag is on.
          const renderTranscriptLine = anchorStyle =>
            FEATURES.DEBUG_OVERLAYS &&
            !isRestStage &&
            !isUpComingStage &&
            !postPerformance &&
            !state.hideText && (
              <InPerformanceText
                text={realtimeVoice.latestAssistantText || state.transcriptText}
                latency={realtimeVoice.latestAssistantLatency}
                hideText={state.openSettingModel}
                containerStyle={anchorStyle}
                isVisible={isTranscriptVisible}
              />
            );

          const videoSide = (
            <VideoAndCommentSide
              inPerformance={inPerformance}
              videoSrc={updatedWithExtensions}
              videoStatus={showConnectionStatus ? connectionView.video : undefined}
              postPerformance={postPerformance}
              videoPlayerRef={videoPlayerRef}
              isFalseStage={isFalseStage}
              counter={display.showsLiveCounter ? displayCounter : null}
              totalReps={totalReps}
              isOnline={display.isOnline}
              videoRef={videoRef}
              SectorCanvasRef={canvasForObjectDetectionRef}
              isRightSide={state.toggleMirror}
              exerciseType={exerciseTypeValue}
              time={state.time}
              totalTime={totalTime}
              canStartUserTimer={canStartUserTimer}
              isStackedMobile={isStackedMobileLayout}
              // Landscape: this side is a card inside the padded grid, and the
              // counter/timer/quality overlay is rendered separately against the
              // camera card instead (see the landscape branch below) — matching
              // where it actually lands on desktop, where `fixed` escapes to the
              // viewport's top-left corner, i.e. the camera cell, not this one.
              fitsContainer={isLandscapeMobile}
              showOverlayReadouts={!isLandscapeMobile}
              isCameraTrackingActive={isCameraTrackingSession}
              currentScore={currentScore}
              showQuality={isQualityReadoutAvailable}
              hasQualityScore={hasQualityScore}
              scoreStyles={scoreStyles}
              currentFps={currentFps}
              frontendFps={state.frontendFps}
              hasFpsOverlay={hasFpsOverlay}
              holdTelemetry={holdTelemetry}
            />
          );

          // webcamSide is the single hoisted camera element declared above the
          // showIntro branch — not redeclared here, so intro and exercise share
          // the same <WebcamAndSkelatonSide> instance.

          if (isStackedMobileLayout) {
            // Camera on top, the counter/quality/timer strip in the middle, and
            // the reference video below. Desktop/tablet/landscape keep the
            // side-by-side grid in the else branch below, where those three
            // values float as overlay pills instead. The colored border wraps
            // only the camera cell, not the reference video.
            return (
              // The camera is a flat 50% of the height. The stat strip and the
              // reference video share the other 50%, with the strip shrink-0 and
              // the video taking what's left — so the strip's height comes out of
              // the reference video, never out of the camera feed.
              <div tw="flex-1 min-h-0 flex flex-col overflow-hidden relative">
                {/* One cell, two styles — deliberately NOT a ternary between
                    two sibling <div>s. Offline the reference video owns the
                    screen and the camera element stays mounted off-screen (see
                    offscreenMountStyle), but a ternary made that a *different*
                    element, so React unmounted the old host and
                    attachCameraPortal appendChild-moved the live <video> into
                    the new one every time showsCameraCell flipped — which is
                    exactly the DOM move that kills the capture session on iOS
                    (see the introOverlay comment above). Same element, same
                    position, only its style changes. */}
                <div
                  css={
                    display.showsCameraCell && tw`relative w-full h-1/2 shrink-0 overflow-hidden`
                  }
                  style={display.showsCameraCell ? undefined : offscreenMountStyle}
                >
                  <div ref={attachCameraPortal} tw="w-full h-full" />
                  {display.showsCameraCell && showBorder && (
                    <InPerformanceBorderContainer shapeColor={borderShapeColor} />
                  )}
                </div>
                <div tw="relative w-full flex-1 min-h-0 flex flex-col overflow-hidden">
                  {/* In-flow, so any status takes its height from the reference
                      video below — never from, or on top of, the camera. */}
                  {showConnectionStatus && (
                    <ConnectionStatusStack
                      status={connectionView}
                      variant="strip"
                      onOpenDetails={openConnectionDetails}
                    />
                  )}
                  <MobileExerciseStatBar
                    showCounter={showMobileCounter}
                    counter={display.showsLiveCounter ? displayCounter : null}
                    totalReps={totalReps}
                    isHoldMode={isMobileHoldMode}
                    showQuality={isMobilePerformanceActive && isQualityReadoutAvailable}
                    score={currentScore}
                    hasScore={hasQualityScore}
                    showTimer={showMobileTimer}
                    timerValue={mobileTimerValue}
                    isCameraCellVisible={display.showsCameraCell}
                  />
                  <div tw="relative w-full flex-1 min-h-0 overflow-hidden">{videoSide}</div>
                </div>
                {renderTranscriptLine(stackedTranscriptStyle)}
              </div>
            );
          }

          if (isLandscapeMobile) {
            // Two rounded cards on a white surface (Figma node 1527-28084 /
            // 1527-28044 / 1527-28002): quality always on the camera card
            // (top-right); the timer joins it there (top-left) only when a
            // counter is also live, in which case the counter takes the video
            // card instead (top-left) — otherwise the timer itself is the video
            // card's top-left readout. Menu stays top-right of the video card
            // (ExerciseTopBar below already positions itself there).
            const landscapeCounterValue = display.showsLiveCounter ? displayCounter || 0 : null;
            const landscapeCounterTotal =
              typeof totalReps === 'number' && totalReps > 0 ? totalReps : null;

            const topBar = (
              <ExerciseTopBar
                isOfflineMode={isOfflineMode}
                isCameraRequired={!showConnectionStatus && display.isCameraMissingForMotionCapture}
                dispatch={dispatch}
                timer={timer}
                sendJsonMessage={sendJsonMessage}
                isUpComingStage={isUpComingStage}
                onCameraInfoOpen={handleCameraInfoOpen}
                onCameraInfoClose={handleCameraInfoClose}
                compact
                statusStack={
                  showConnectionStatus && (
                    <ConnectionStatusStack
                      status={connectionView}
                      onOpenDetails={openConnectionDetails}
                      compact
                    />
                  )
                }
                detailsButton={connectionDetailsButton}
                detailsPanel={
                  showConnectionStatus && (
                    <ConnectionDetailsPanel
                      status={connectionView}
                      open={isConnectionDetailsOpen}
                      onClose={closeConnectionDetails}
                    />
                  )
                }
              />
            );

            return (
              <div
                css={[
                  landscapeSurfaceStyle,
                  display.showsCameraCell ? tw`grid-cols-2` : tw`grid-cols-1`,
                ]}
              >
                {/* Same single-element rule as the stacked branch above: the
                    camera's host div must never be swapped for a different one,
                    only restyled. */}
                <div
                  css={display.showsCameraCell && landscapeCardStyle}
                  style={display.showsCameraCell ? undefined : offscreenMountStyle}
                >
                  <div ref={attachCameraPortal} tw="w-full h-full" />
                  {display.showsCameraCell && (
                    <>
                      {showBorder && <InPerformanceBorderContainer shapeColor={borderShapeColor} />}
                      {cameraShowsLandscapeTimer && (
                        <div
                          tw="absolute top-2 left-2 z-[100]"
                          style={{ transform: 'scale(0.85)', transformOrigin: 'top left' }}
                        >
                          <CutDownTimer
                            timer={landscapeTimerValue}
                            exerciseType={exerciseTypeValue}
                          />
                        </div>
                      )}
                      {showLandscapeQuality && (
                        <div
                          tw="absolute top-2 right-2 z-[100]"
                          style={{ transform: 'scale(0.85)', transformOrigin: 'top right' }}
                        >
                          <QualityChip score={currentScore} hasScore={hasQualityScore} />
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div css={landscapeCardStyle}>
                  {videoSide}
                  {showLandscapeCounter && (
                    <div
                      tw="absolute top-2 left-2 z-[100]"
                      style={{ transform: 'scale(0.85)', transformOrigin: 'top left' }}
                    >
                      <Counter
                        counterCurrentValue={landscapeCounterValue}
                        counterTotal={landscapeCounterTotal}
                        isFalseStage={isFalseStage}
                      />
                    </div>
                  )}
                  {videoShowsLandscapeTimer && (
                    <div
                      tw="absolute top-2 left-2 z-[100]"
                      style={{ transform: 'scale(0.85)', transformOrigin: 'top left' }}
                    >
                      <CutDownTimer timer={landscapeTimerValue} exerciseType={exerciseTypeValue} />
                    </div>
                  )}
                  {topBar}
                </div>
                {renderTranscriptLine(landscapeTranscriptStyle)}
              </div>
            );
          }

          // Camera first, reference video second — the border wraps only the
          // camera cell, not the reference video. Unchanged from before the
          // mobile work: this branch owns its own h-screen, as it always did.
          return (
            <div
              tw="h-screen grid overflow-hidden relative"
              css={display.showsCameraCell ? tw`grid-cols-2` : tw`grid-cols-1`}
            >
              {/* Same single-element rule as the two mobile branches above. */}
              <div
                css={display.showsCameraCell && tw`relative w-full h-full min-h-0`}
                style={display.showsCameraCell ? undefined : offscreenMountStyle}
              >
                <div ref={attachCameraPortal} tw="w-full h-full" />
                {display.showsCameraCell && showBorder && (
                  <InPerformanceBorderContainer shapeColor={borderShapeColor} />
                )}
              </div>
              {videoSide}
              {renderTranscriptLine(desktopTranscriptStyle)}
            </div>
          );
        })()}

        <AudioSection
          audioDevice={audioDevice}
          currentCounter={displayCounter}
          getReadyCountdownValue={preStartCountdownValue}
          dispatch={dispatch}
          soundRef={soundRef}
          voiceResult={voiceResult}
          audioSectionRef={audioSectionRef}
          isRealtimeActive={isRealtimeActive}
          isSessionEnding={isPostStage}
          // False the moment the pause modal opens OR the stage leaves
          // IN_PERFORMANCE — a restart drops back to INTRO, and the get-ready
          // clip has no business outliving either.
          isGetReadyWindowOpen={shouldShowPreStartCountdown}
        />

        <RestAndNextConfiguration
          isRestStage={isRestStage}
          exerciseType={exerciseTypeValue}
          state={state}
          data={data}
          updatedWithExtensions={updatedWithExtensions}
          currentTimeLeft={state.currentTimeLeft}
          postData={postData}
          postPerformance={postPerformance}
          references={{ videoRef, soundRef }}
          handlers={{
            setReconnect,
            dispatch,
          }}
          isComingUp={isUpComingStage}
          inPerformance={inPerformance}
          time={state.time}
          timerControl={timer}
          sendJsonMessage={sendJsonMessage}
          initialConfig={initialConfig}
          setResult={setResult}
          upComingStageResult={upComingStageResult}
          exerciseSummary={exerciseSummary}
          showFormQuality={isCameraScoringAllowed}
          onExit={() => {
            clearLatestRecordingPayload().catch(() => {});
            navigate('/home', { replace: true });
            closeCameraAfterNavigate();
            removeFromLocalStorageInPerformance();
          }}
        />
        {/* Above RestUiSection (z-[100]) and below the settings modal
            (z-[9999]) — same stacking the early-return version had. */}
        {introOverlay}
        {showConnectionStatus && isStackedMobileLayout && (
          <ConnectionDetailsPanel
            status={connectionView}
            open={isConnectionDetailsOpen}
            onClose={closeConnectionDetails}
            variant="sheet"
          />
        )}
        {settingModalNode}
      </div>
    </>
  );
};

export default ExercisePage;
